/**
 * 🆕 Phase 64 — Helpers pour le badge "Depuis X jours" sur les factures impayées
 *
 * Fichier séparé (pas de dépendance React/Tailwind) → testable en Jest unitaire.
 * Importé par SectionPaiements.jsx.
 */

/** Retourne le nombre de jours écoulés depuis l'envoi de la facture, ou null si inconnu. */
export function getJoursDepuisEnvoi(dateEnvoi) {
  if (!dateEnvoi) return null;
  const sent = new Date(dateEnvoi);
  if (isNaN(sent.getTime())) return null;
  const diffMs = Date.now() - sent.getTime();
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/** Retourne les classes Tailwind du badge selon le nombre de jours (3 seuils). */
export function getBadgeRetardClasses(jours) {
  if (jours === null || jours === undefined) return null;
  // 🟢 0 à 14 jours = récente
  if (jours < 15) return 'bg-green-100 text-green-800 border-green-300';
  // 🟡 15 à 29 jours = à relancer
  if (jours < 30) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  // 🔴 30 jours et plus = URGENT
  return 'bg-red-100 text-red-800 border-red-300';
}

/** Retourne le texte du badge : "il y a Xj" + ⚠️ si urgent. */
export function formatBadgeRetardText(jours) {
  if (jours === null || jours === undefined) return '';
  if (jours >= 30) return `il y a ${jours}j ⚠️`;
  return `il y a ${jours}j`;
}

// 🆕 [Phase 92 — 2026-07-08] Nouveaux helpers pour l'historique unifié envois

/**
 * Retourne les jours depuis le PREMIER envoi (evite le reset lors de renvoi).
 * Fallback rétrocompat : si pas d'historique, utilise dateEnvoiEmail.
 */
export function getJoursDepuisPremierEnvoi(premierEnvoiDate, dateEnvoiEmailFallback) {
  return getJoursDepuisEnvoi(premierEnvoiDate || dateEnvoiEmailFallback);
}

/**
 * Retourne un résumé compact des envois : "3 envois (dernier: il y a 1j)".
 * Compte tous les envois (email + SMS unifiés) — Phase 92 Option Intégrée.
 */
export function formatResumeEnvois(nbEnvoisTotal, dernierEnvoiDate) {
  if (!nbEnvoisTotal || nbEnvoisTotal < 1) return '';
  const jours = getJoursDepuisEnvoi(dernierEnvoiDate);
  if (nbEnvoisTotal === 1) return `1 envoi${jours !== null ? ` (il y a ${jours}j)` : ''}`;
  return `${nbEnvoisTotal} envois${jours !== null ? ` (dernier : il y a ${jours}j)` : ''}`;
}

/**
 * Retourne un aperçu compact des 2-3 derniers envois pour tooltip / affichage :
 * "📧 il y a 30j, 📧 il y a 15j, 📱 il y a 1j"
 */
export function formatApercuEnvois(historiqueEnvoisRecents) {
  if (!Array.isArray(historiqueEnvoisRecents) || historiqueEnvoisRecents.length === 0) return '';
  return historiqueEnvoisRecents
    .slice(0, 3)
    .map((e) => {
      const icone = e.mode === 'sms' ? '📱' : '📧';
      const jours = getJoursDepuisEnvoi(e.date);
      return jours !== null ? `${icone} il y a ${jours}j` : `${icone}`;
    })
    .join(', ');
}
